import { conversationSchema, type Conversation } from '@growup/streaming-shared';
import { Button, Field, Notice, Textarea } from '@growup/ui';
import { useCallback, useEffect, useRef, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { formatDateTime } from '../format';

/**
 * A conversa privada streamer-padrinho (GS13). The padrinho (`canWrite`)
 * reads and replies; the Stream Staff only reads. Opening it marks it as read
 * for the padrinho.
 */
export function ConversationView({
  path,
  canWrite,
  onRead,
}: {
  path: string;
  canWrite: boolean;
  onRead?: () => void;
}) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // onRead muda a cada render do pai; so o caminho volta a carregar.
  const readRef = useRef(onRead);
  readRef.current = onRead;

  const load = useCallback(() => {
    setError(null);
    getJson(path, conversationSchema)
      .then((c) => {
        setConversation(c);
        readRef.current?.();
      })
      .catch((e: unknown) => setError(messageOf(e)));
  }, [path]);
  useEffect(load, [load]);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      setConversation(conversationSchema.parse(await send('POST', path, { body })));
      setBody('');
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-3" aria-label="Conversa">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {conversation && conversation.messages.length === 0 ? (
        <p className="text-gu-ink-muted">Ainda não há mensagens.</p>
      ) : null}
      <ul className="flex flex-col gap-2 border-l-2 border-gu-line pl-3">
        {conversation?.messages.map((m) => (
          <li key={m.id} className="flex flex-col">
            <span className="text-caption text-gu-ink-muted">
              {m.from === 'me' ? 'Eu' : m.author} · {formatDateTime(m.createdAt)}
            </span>
            <span className="whitespace-pre-line">{m.body}</span>
          </li>
        ))}
      </ul>
      {canWrite ? (
        <div className="flex flex-col gap-2">
          <Field label="Responder">
            <Textarea
              value={body}
              rows={3}
              maxLength={2000}
              onChange={(e) => setBody(e.target.value)}
            />
          </Field>
          <div>
            <Button size="sm" disabled={busy || body.trim() === ''} onClick={() => void submit()}>
              Enviar
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
